import type { ApprovalLetterCorpusResult } from '../types';
import { regulatoryCitationHref } from '../utils/regulatoryCitationHref';

interface ApprovalLetterResultsProps {
  results: ApprovalLetterCorpusResult[];
}

function submissionLabel(result: ApprovalLetterCorpusResult): string {
  const type = result.submission_type.toUpperCase();
  if (type === 'ORIG') return 'Original';
  if (type === 'SUPPL') return `Supplement ${result.submission_number}`;
  return `${result.submission_type} ${result.submission_number}`.trim();
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00Z`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export default function ApprovalLetterResults({ results }: ApprovalLetterResultsProps) {
  if (results.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-base font-medium text-slate-600">No FDA Approval Letters found</p>
        <p className="mt-1 text-sm text-slate-400">Try adjusting your search terms.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {results.map((result) => {
        const documentHref = regulatoryCitationHref({
          document_type: 'approval_letter',
          drug_id: result.viewer_drug_id,
          document_id: result.document_id,
        });
        const letterDate = formatDate(result.letter_date);
        const statusDate = formatDate(result.submission_status_date);

        return (
          <article
            key={result.result_key || `approval_letter:${result.document_id}`}
            className="rounded-xl border border-slate-200 bg-white px-5 py-4 transition-all hover:border-indigo-200 hover:shadow-md"
          >
            <div className="flex items-start gap-4">
              <div className="min-w-0">
                {documentHref ? (
                  <a
                    href={documentHref}
                    className="text-base font-semibold text-slate-800 transition-colors hover:text-indigo-700"
                  >
                    {result.trade_name || `${result.application_type} ${result.application_number}`}
                  </a>
                ) : (
                  <span className="text-base font-semibold text-slate-800">
                    {result.trade_name || `${result.application_type} ${result.application_number}`}
                  </span>
                )}
                {result.sponsor_name && (
                  <p className="mt-0.5 text-sm text-slate-500">{result.sponsor_name}</p>
                )}
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-500">
              <span>{result.application_type} {result.application_number}</span>
              <span>{submissionLabel(result)}</span>
              {letterDate && <span>Letter {letterDate}</span>}
              {statusDate && <span>Status {statusDate}</span>}
            </div>

            {result.evidence.length > 0 && (
              <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                {result.evidence.slice(0, 3).map((evidence) => {
                  const evidenceHref = regulatoryCitationHref({
                    document_type: 'approval_letter',
                    drug_id: result.viewer_drug_id,
                    document_id: result.document_id,
                    chunk_id: evidence.chunk_id,
                    page: evidence.page_num,
                  });
                  const label = evidence.page_num == null
                    ? 'Supporting evidence'
                    : `Page ${evidence.page_num}`;
                  return (
                    <div key={evidence.chunk_id}>
                      {evidenceHref && (
                        <a
                          href={evidenceHref}
                          className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                        >
                          {label}
                        </a>
                      )}
                      <p className="mt-0.5 text-sm leading-relaxed text-slate-600">
                        {evidence.snippet}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
